import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useRates } from "@/context/RatesContext";
import { formatINR } from "@/lib/api";
import { LOAN_TYPES, PREFILL_KEY, calcEmi, calcMaxLoan, slug } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { ArrowRight, CheckCircle2, AlertTriangle } from "lucide-react";

const Row = ({ label, value, testId, children }) => (
  <div>
    <div className="flex justify-between text-sm">
      <span className="text-slate-300">{label}</span>
      <span className="font-semibold text-white" data-testid={testId}>{value}</span>
    </div>
    <div className="mt-3">{children}</div>
  </div>
);

export const EligibilityCalculator = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { rates: RATES } = useRates();
  const [loanType, setLoanType] = useState("Home Loan");
  const [rate, setRate] = useState(RATES["Home Loan"]);
  useEffect(() => { setRate(RATES[loanType]); }, [RATES, loanType]);
  const [amount, setAmount] = useState(12500000);
  const [tenure, setTenure] = useState(20);
  const [income, setIncome] = useState(350000);

  const { emi, maxLoan, eligible } = useMemo(() => {
    const e = calcEmi(amount, rate, tenure);
    const m = calcMaxLoan(income, rate, tenure);
    return { emi: e, maxLoan: m, eligible: amount <= m };
  }, [amount, rate, tenure, income]);

  const apply = () => {
    sessionStorage.setItem(PREFILL_KEY, JSON.stringify({ loan_type: loanType, requested_amount: amount, tenure_years: tenure, monthly_income: income, interest_rate: rate }));
    navigate(user ? (user.role === "admin" ? "/admin" : "/apply") : "/register");
  };

  return (
    <div className="navy-glass rounded-2xl p-6 sm:p-8 grid lg:grid-cols-5 gap-8" data-testid="eligibility-calculator">
      <div className="lg:col-span-3 space-y-7">
        <div className="flex flex-wrap gap-2" data-testid="calc-loan-type-group">
          {LOAN_TYPES.map((t) => (
            <button key={t} type="button" onClick={() => setLoanType(t)} data-testid={`calc-loan-type-${slug(t)}`}
              className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${loanType === t ? "bg-[#FF6B00] text-[#070D1E]" : "bg-white/10 text-slate-200 hover:bg-white/20"}`}>
              {t} · {RATES[t].toFixed(2)}%
            </button>
          ))}
        </div>
        <Row label="Loan amount" value={formatINR(amount)} testId="calc-amount-value">
          <Slider value={[amount]} min={500000} max={250000000} step={500000} onValueChange={([v]) => setAmount(v)} data-testid="calc-amount-slider" className="[&_[role=slider]]:bg-[#FF6B00] [&_[role=slider]]:border-[#FF6B00] [&_.bg-primary]:bg-[#FF6B00]" />
        </Row>
        <Row label="Tenure" value={`${tenure} years`} testId="calc-tenure-value">
          <Slider value={[tenure]} min={1} max={30} step={1} onValueChange={([v]) => setTenure(v)} data-testid="calc-tenure-slider" className="[&_[role=slider]]:bg-[#FF6B00] [&_[role=slider]]:border-[#FF6B00]" />
        </Row>
        <Row label="Interest rate" value={`${rate.toFixed(2)}% p.a.`} testId="calc-rate-value">
          <div className="flex items-center gap-4">
            <Slider value={[rate]} min={6} max={18} step={0.05} onValueChange={([v]) => setRate(v)} data-testid="calc-rate-slider" className="flex-1 [&_[role=slider]]:bg-[#FF6B00] [&_[role=slider]]:border-[#FF6B00]" />
            <input type="number" min="6" max="18" step="0.05" value={rate} onChange={(e) => setRate(Math.min(18, Math.max(6, Number(e.target.value) || 6)))} data-testid="calc-rate-input"
              className="w-20 h-9 rounded-md bg-white/10 border border-orange-500/30 text-white text-sm text-center focus:outline-none focus:ring-2 focus:ring-[#FF6B00]" />
          </div>
          {rate !== RATES[loanType] && (
            <button type="button" onClick={() => setRate(RATES[loanType])} className="mt-2 text-xs text-[#FF6B00] hover:underline" data-testid="calc-rate-reset">Reset to {loanType} rate ({RATES[loanType].toFixed(2)}%)</button>
          )}
        </Row>
        <Row label="Net monthly income" value={formatINR(income)} testId="calc-income-value">
          <Slider value={[income]} min={25000} max={5000000} step={25000} onValueChange={([v]) => setIncome(v)} data-testid="calc-income-slider" className="[&_[role=slider]]:bg-[#FF6B00] [&_[role=slider]]:border-[#FF6B00]" />
        </Row>
      </div>
      <div className="lg:col-span-2 bg-white text-[#0F172A] rounded-xl p-6 flex flex-col justify-between">
        <div>
          <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Indicative EMI</p>
          <p className="mt-1 font-display text-3xl font-bold" data-testid="calc-emi-value">{formatINR(emi)}<span className="text-sm font-normal text-slate-500"> /mo</span></p>
          <p className="mt-1 text-xs text-slate-500">indicative · best partner-bank rate at {rate.toFixed(2)}% p.a. over {tenure} years</p>
          <div className="mt-5 border-t border-slate-100 pt-4">
            <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">You are eligible up to</p>
            <p className="mt-1 font-display text-2xl font-semibold text-[#E05E00]" data-testid="calc-max-loan-value">{formatINR(maxLoan)}</p>
          </div>
          <p className={`mt-4 flex items-center gap-2 text-sm font-medium ${eligible ? "text-emerald-600" : "text-amber-600"}`} data-testid="calc-eligibility-status">
            {eligible ? <><CheckCircle2 className="h-4 w-4" /> Requested amount is within eligibility</> : <><AlertTriangle className="h-4 w-4" /> Reduce amount or extend tenure</>}
          </p>
        </div>
        <Button onClick={apply} className="gold-btn rounded-full h-11 mt-6 w-full" data-testid="calc-apply-button">
          Apply with these details <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
      </div>
    </div>
  );
};
